import React from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Grid,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { ChevronDown } from 'lucide-react';
import {
  FetchAllBusinessModel,
  FetchAllCategories,
  FetchAllInvestmentSize,
  FetchAllStages,
  FetchAllSubCategories,
} from 'api/utils';
import { OptionChipGroup } from 'ui-components/OptionChip';
import GeoMultiSelect from 'ui-components/GeoMultiSelect';
import CustomValidationText from 'ui-components/CustomValidationText';
import { getMarks } from 'utils/index';
import {
  autocompleteSlotSx,
  BORDER,
  FieldLabel,
  INDIGO,
  SectionTitle,
  selectSx,
  TEXT_COLOR,
} from '../primitives';

// Same option set as the old wizard's StartupInvestments: "11" means
// "Invested in more than 10 startups" (the Figma's "10 +"), "0" means new to investing.
const INVESTMENT_COUNTS = [
  { value: '0', label: '0' },
  { value: '01', label: '01' },
  { value: '02', label: '02' },
  { value: '03', label: '03' },
  { value: '04', label: '04' },
  { value: '05', label: '05' },
  { value: '06', label: '06' },
  { value: '07', label: '07' },
  { value: '08', label: '08' },
  { value: '09', label: '09' },
  { value: '10', label: '10' },
  { value: '11', label: '10 +' },
];

const InlineCheck = ({ checked, onChange, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography sx={{ fontSize: '0.875rem', color: TEXT_COLOR }}>{label}</Typography>
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{ p: 0.5, color: '#D0D5DD', '&.Mui-checked': { color: INDIGO } }}
    />
  </Box>
);

const InvestmentInterest = ({
  isSectorAgnostic,
  setIsSectorAgnostic,
  isBusinessModelSelectAll,
  setIsBusinessModelSelectAll,
}) => {
  const formik = useFormikContext();
  const { values, errors, setFieldError } = formik;
  // validateOnChange is off, so an error set on "Next" would otherwise linger even
  // after the field is filled. Clear a field's own error the moment its value changes.
  const setFieldValue = (name, value, ...rest) => {
    formik.setFieldValue(name, value, ...rest);
    if (errors[name]) setFieldError(name, undefined);
  };

  const { data: categories } = FetchAllCategories();
  const { data: businessModels, isLoading: isLoadingBusinessModels } = FetchAllBusinessModel({});
  const { data: stages } = FetchAllStages();
  const { data: investmentSizeList, isLoading: isLoadingInvestmentSize } = FetchAllInvestmentSize();

  // Sub-categories are fetched by category NAME, not id — same as the old wizard.
  const selectedCategoryNames = (categories || [])
    .filter((c) => (values.category_id || []).includes(c?._id))
    .map((c) => c?.name);
  const { data: subCategories, isLoading: isLoadingSubCategories } = FetchAllSubCategories({
    category: selectedCategoryNames,
  });

  const currency = values.investment_amount_type || 'INR';
  const marks = getMarks(currency);
  const currencyOptions = investmentSizeList?.currencies || [];

  // Default the currency and the range once the size list lands (old wizard's InvestmentRange).
  React.useEffect(() => {
    if (isLoadingInvestmentSize || !investmentSizeList) return;
    if (!values.investment_amount_type) {
      setFieldValue('investment_amount_type', investmentSizeList.currencies?.[0]?.name || 'INR');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingInvestmentSize, investmentSizeList]);

  // The slider visually falls back to [0,3] (the default range) whenever min/max
  // are null, so it LOOKS filled. enableReinitialize (profile refetch) can blank
  // min/max after this ran, so also re-default when they go null — otherwise the
  // shown default isn't in formik and "Please select a range" fires until moved.
  React.useEffect(() => {
    if (!marks?.length) return;
    if (values.min == null || values.max == null) {
      setFieldValue('min', marks[0].selectedValue);
      setFieldValue('max', marks[3].selectedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, values.min, values.max]);

  // The slider works in mark indexes; formik stores the mark's selectedValue.
  const sliderValue = [
    Math.max(0, marks.findIndex((m) => m.selectedValue === values.min)),
    (() => {
      const i = marks.findIndex((m) => m.selectedValue === values.max);
      return i === -1 ? 3 : i;
    })(),
  ];

  const handleSliderChange = (event, next) => {
    if (!Array.isArray(next)) return;
    setFieldValue('min', marks[next[0]].selectedValue);
    setFieldValue('max', marks[next[1]].selectedValue);
  };

  const handleCurrencyChange = (event) => {
    const nextCurrency = event.target.value;
    const nextMarks = getMarks(nextCurrency);
    setFieldValue('investment_amount_type', nextCurrency);
    setFieldValue('min', nextMarks[0].selectedValue);
    setFieldValue('max', nextMarks[3].selectedValue);
  };

  const sectorOptions = (subCategories || []).map((item) => ({
    label: item?.sub_category,
    value: item?._id,
    description: item?.description,
  }));
  const selectedSectors = sectorOptions.filter((o) => (values.sector_id || []).includes(o.value));

  const businessModelOptions = (businessModels || []).map((item) => ({
    label: item?.name,
    value: item?._id,
    description: item?.description,
  }));
  const selectedBusinessModels = businessModelOptions.filter((o) =>
    (values.business_model_id || []).includes(o.value)
  );

  const multiAutocompleteProps = {
    multiple: true,
    disableCloseOnSelect: true,
    getOptionLabel: (o) => o?.label || '',
    isOptionEqualToValue: (o, v) => o?.value === v?.value,
    popupIcon: <ChevronDown size={18} color="#667085" />,
    renderOption: (props, option) => (
      <li {...props} key={option.value} style={{ display: 'grid' }}>
        <span style={{ fontWeight: 600 }}>{option.label}</span>
        {option.description && (
          <span style={{ fontSize: '0.8rem', color: '#667085' }}>{option.description}</span>
        )}
      </li>
    ),
    renderTags: (value, getTagProps) =>
      value.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.value}
          label={option.label}
          sx={{
            borderRadius: '8px',
            background: '#EEF0FF',
            color: TEXT_COLOR,
            fontWeight: 500,
            border: `1px solid ${INDIGO}`,
          }}
        />
      )),
  };

  return (
    <>
      <SectionTitle>Preferred Startup</SectionTitle>

      {/* -------------------------------- Category -------------------------------- */}
      <Box sx={{ mb: 3.5 }}>
        <FieldLabel required>Select Category (Multi Select)</FieldLabel>
        <OptionChipGroup
          options={categories || []}
          multiple
          indicator="checkbox"
          value={values.category_id || []}
          getOptionValue={(o) => o?._id}
          getOptionLabel={(o) => o?.name}
          onChange={(next) => {
            setFieldValue('category_id', next);
            // Sectors hang off the chosen categories — drop any that no longer apply.
            setFieldValue('sector_id', []);
          }}
        />
        {errors.category_id && <CustomValidationText value={errors.category_id} />}
      </Box>

      {/* --------------------------- Sectors + Business model --------------------------- */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}
          >
            <FieldLabel sx={{ mb: 0 }}>Select Sectors (Multi Select)</FieldLabel>
            <InlineCheck
              label="Sector Agnostic"
              checked={isSectorAgnostic}
              onChange={(checked) => {
                setIsSectorAgnostic(checked);
                if (checked) setFieldValue('sector_id', []);
              }}
            />
          </Box>
          <Autocomplete
            {...multiAutocompleteProps}
            options={sectorOptions}
            value={selectedSectors}
            disabled={isSectorAgnostic}
            loading={isLoadingSubCategories}
            noOptionsText={
              selectedCategoryNames.length ? 'No Options' : 'Select a category first'
            }
            onChange={(e, next) =>
              setFieldValue('sector_id', next.map((o) => o.value))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                placeholder={isSectorAgnostic ? 'Sector Agnostic' : 'Select sectors'}
                error={!!errors.sector_id}
                sx={autocompleteSlotSx}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
              />
            )}
          />
          {errors.sector_id && <CustomValidationText value={errors.sector_id} />}
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}
          >
            <FieldLabel sx={{ mb: 0 }}>Select Business Model (Multi Select)</FieldLabel>
            <InlineCheck
              label="Select All"
              checked={isBusinessModelSelectAll}
              onChange={(checked) => {
                setIsBusinessModelSelectAll(checked);
                if (checked) setFieldValue('business_model_id', []);
              }}
            />
          </Box>
          <Autocomplete
            {...multiAutocompleteProps}
            options={businessModelOptions}
            value={selectedBusinessModels}
            disabled={isBusinessModelSelectAll}
            loading={isLoadingBusinessModels}
            noOptionsText="No Options"
            onChange={(e, next) =>
              setFieldValue('business_model_id', next.map((o) => o.value))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                placeholder={
                  isBusinessModelSelectAll ? 'All' : 'Search or select business model'
                }
                error={!!errors.business_model_id}
                sx={autocompleteSlotSx}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
              />
            )}
          />
          {errors.business_model_id && <CustomValidationText value={errors.business_model_id} />}
        </Grid>
      </Grid>

      {/* ------------------------------- GeoMultiSelect -------------------------------
          Not in the Figma, but a shipped feature: it feeds deal-flow matching and
          AI shortlisting. Same fields the old wizard writes. */}
      <Box sx={{ mb: 3.5 }}>
        {/* <GeoMultiSelect
          countryIds={values.preferred_country_id || []}
          stateIds={values.preferred_state_id || []}
          cityIds={values.preferred_city_id || []}
          onChange={({ countryIds, stateIds, cityIds }) => {
            setFieldValue('preferred_country_id', countryIds);
            setFieldValue('preferred_state_id', stateIds);
            setFieldValue('preferred_city_id', cityIds);
          }}
        /> */}
      </Box>

      <Box sx={{ my: 4, borderTop: `1px solid ${BORDER}` }} />

      {/* -------------------------------- Amount range -------------------------------- */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <FieldLabel sx={{ mb: 0 }}>Select amount range per startup on</FieldLabel>
          <Select
            value={currency}
            onChange={handleCurrencyChange}
            variant="filled"
            disableUnderline
            IconComponent={(p) => <ChevronDown {...p} size={18} color="#667085" />}
            sx={{ ...selectSx, width: 'auto', minWidth: 96, flexShrink: 0 }}
          >
            {(currencyOptions.length ? currencyOptions : [{ name: 'INR' }]).map((item) => (
              <MenuItem key={item.name} value={item.name}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ px: 1.5 }}>
          <Slider
            value={sliderValue}
            min={0}
            max={marks.length - 1}
            step={1}
            marks={marks}
            onChange={handleSliderChange}
            disableSwap
            sx={{
              mt: 5,
              color: INDIGO,
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
                backgroundColor: '#fff',
                border: `2px solid ${INDIGO}`,
              },
              '& .MuiSlider-track': { height: 8, borderRadius: 4, border: 'none' },
              '& .MuiSlider-rail': { height: 8, borderRadius: 4, opacity: 1, background: '#E4E7EC' },
              '& .MuiSlider-mark': { display: 'none' },
              '& .MuiSlider-markLabel': {
                fontSize: '0.8rem',
                color: '#667085',
                top: '-18px !important',
              },
            }}
          />
        </Box>
        {(errors.min || errors.max) && <CustomValidationText value={errors.min || errors.max} />}
      </Box>

      {/* ------------------------------- Preferred stages ------------------------------- */}
      <Box sx={{ mb: 3.5 }}>
        <FieldLabel required>Preferred Stages</FieldLabel>
        <OptionChipGroup
          options={stages || []}
          multiple
          indicator="checkbox"
          value={values.stage_id || []}
          getOptionValue={(o) => o?._id}
          getOptionLabel={(o) => o?.name}
          onChange={(next) => setFieldValue('stage_id', next)}
        />
        {errors.stage_id && <CustomValidationText value={errors.stage_id} />}
      </Box>

      {/* ------------------------------ Total investments ------------------------------ */}
      <Box>
        <FieldLabel>Total investments Made</FieldLabel>
        <OptionChipGroup
          options={INVESTMENT_COUNTS}
          indicator="check"
          value={values.investment_count}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          onChange={(next) => setFieldValue('investment_count', next)}
        />
        {errors.investment_count && <CustomValidationText value={errors.investment_count} />}
      </Box>
    </>
  );
};

export default InvestmentInterest;
